import { computed, type ComputedRef } from 'vue'

import type { ApprovalInboxRow } from '../models/platformAction.types'
import {
  canDecide,
  isViewerRank,
  offersCancel,
  resolveRank,
  showsControls,
  WORKSPACE_RANK,
  type ApprovalViewer,
  type WorkspaceRank,
} from '../service/approvalEligibility'

import { useAccount } from '@/features/account'

export interface UseApprovalEligibility {
  viewer: ComputedRef<ApprovalViewer>
  /** Ladder rank of the signed-in user in the selected account, owner-aware. */
  rank: ComputedRef<number>
  isViewer: ComputedRef<boolean>
  atLeast: (threshold: WorkspaceRank) => boolean
  canDecide: (row: ApprovalInboxRow) => boolean
  offersCancel: (row: ApprovalInboxRow) => boolean
  showsControls: (row: ApprovalInboxRow) => boolean
  /** True when `id` is the signed-in user — "waiting on you" vs "waiting on them". */
  isMe: (id: number | null | undefined) => boolean
}

/**
 * The approver-side reading of the signed-in user. `userId` is the AUTHENTICATED user, never
 * `accountUserId` — that one is the selected account's OWNER, used here only to recognise them.
 */
export function useApprovalEligibility(): UseApprovalEligibility {
  const account = useAccount()

  const isAccountOwner = computed(() => {
    const ownerId = account.accountUserId.value
    const userId = account.userId.value
    return ownerId !== undefined && userId !== undefined && String(ownerId) === String(userId)
  })

  const rank = computed(() => resolveRank(account.accountLevel.value, isAccountOwner.value))
  const viewer = computed<ApprovalViewer>(() => ({
    rank: rank.value,
    userId: account.userId.value ?? null,
  }))

  return {
    viewer,
    rank,
    isViewer: computed(() => isViewerRank(rank.value)),
    atLeast: (threshold) => rank.value >= (WORKSPACE_RANK[threshold] ?? 0),
    canDecide: (row) => canDecide(row, viewer.value),
    offersCancel,
    showsControls: (row) => showsControls(row, viewer.value),
    isMe: (id) => id !== null && id !== undefined && id === viewer.value.userId,
  }
}
